import React, { useEffect, useRef, useState } from "react";
import { useEscape } from "@/hooks/useEscape";
import { api } from "@/lib/api";
import Message from "@/components/Message";
import { X, Send } from "lucide-react";
import { toast } from "sonner";

export default function ThreadPanel({ parent, workspaceId, currentUser, subscribe, onClose, onReact, onEdit, onDelete }) {
  useEscape(true, () => onClose?.());
  const [replies, setReplies] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/messages", { params: { workspace_id: workspaceId, parent_id: parent.id } }).then((r) => setReplies(r.data));
  }, [parent.id, workspaceId]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((ev) => {
      const m = ev.data;
      if (!m) return;
      if (ev.type === "message" && m.parent_id === parent.id) {
        setReplies((prev) => prev.find((x) => x.id === m.id) ? prev : [...prev, m]);
      } else if (ev.type === "message_update" && (m.parent_id === parent.id || m.id === parent.id)) {
        setReplies((prev) => prev.map((x) => x.id === m.id ? m : x));
      } else if (ev.type === "message_delete") {
        if (m.id === parent.id) onClose?.();           // the thread's root was deleted
        else setReplies((prev) => prev.filter((x) => x.id !== m.id));
      }
    });
  }, [subscribe, parent.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [replies]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { workspace_id: workspaceId, content: text, parent_id: parent.id });
      setText("");
    } catch { toast.error("Nu a reușit"); }
    finally { setSending(false); }
  };

  return (
    <aside className="fixed inset-0 z-40 w-full flex flex-col bg-bg 2xl:static 2xl:w-[420px]" data-testid="thread-panel">
      <div className="h-14 px-4 flex items-center justify-between border-b border-line/10">
        <div>
          <p className="label-tiny">// FIR</p>
          <p className="text-sm text-fg/70 truncate max-w-[280px]">răspuns la {parent.sender_name}</p>
        </div>
        <button data-testid="thread-close-button" aria-label="Închide firul" onClick={onClose} className="text-fg/40 hover:text-fg">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        <Message msg={parent} compact={false} currentUserId={currentUser.id} onReact={(e) => onReact(parent.id, e)}
          onEdit={onEdit && ((content) => onEdit(parent.id, content))} onDelete={onDelete && (() => onDelete(parent.id))} />
        {replies.length > 0 && <div className="border-t border-line/10 pt-2 label-tiny">// {replies.length} {replies.length === 1 ? "reply" : "replies"}</div>}
        {replies.map((r) => (
          <Message key={r.id} msg={r} compact={false} currentUserId={currentUser.id} onReact={(e) => onReact(r.id, e)}
            onEdit={onEdit && ((content) => onEdit(r.id, content))} onDelete={onDelete && (() => onDelete(r.id))} />
        ))}
      </div>
      <form onSubmit={send} className="border-t border-line/10 p-3">
        <div className="bg-surface-2 border border-line/10 focus-within:border-line/30">
          <textarea
            data-testid="thread-input"
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Răspunde…" rows={2}
            className="w-full bg-transparent text-fg text-sm p-3 outline-none resize-none placeholder-fg/30"
          />
          <div className="flex justify-end px-3 pb-2">
            <button data-testid="thread-send-button" type="submit" disabled={sending || !text.trim()} className="btn-primary text-sm flex items-center gap-1.5 px-3 py-1">
              Răspunde <Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      </form>
    </aside>
  );
}
